import React from 'react'
import { Form, Formik } from 'formik'
import * as yup from 'yup'
import InputFieldOutlined from 'component/Inputs/dashboard-input'
import Select from 'component/Select/select'
import Button from 'component/Buttons/login-button'
import SecondaryBtn from 'component/Buttons/secondary-btn'
import { Info } from 'lucide-react'
import Api from 'utils/api'
import { useOperator } from 'context/operator'

const LabelWithInfo = ({ label, info }) => (
  <span className='flex items-center gap-1.5'>
    <span className='text-sm font-semibold text-gray-700'>{label}</span>
    <span className='group/info relative flex items-center'>
      <Info className='w-3.5 h-3.5 text-gray-400 cursor-help hover:text-[#BA141B] transition-colors' />
      <span className='absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 p-2 bg-gray-900 text-white text-[10px] leading-tight rounded-lg opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all z-50 pointer-events-none shadow-xl font-normal'>
        {info}
        <span className='absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-8 border-transparent border-t-gray-900'></span>
      </span>
    </span>
  </span>
)

const PlanValidationSchema = yup.object({
  productname: yup.string().required('Plan name is required'),
  description: yup.string(),
  currency: yup.string().required('Currency is required'),
  interval: yup.string(),
  unlockFee: yup.number().transform((value, originalValue) => originalValue === '' ? 0 : value).min(0, 'Must be 0 or greater'),
  guestBikeUnlockFee: yup.number().transform((value, originalValue) => originalValue === '' ? 0 : value).min(0, 'Must be 0 or greater'),
  unitFee: yup.number().transform((value, originalValue) => originalValue === '' ? 0 : value).min(0, 'Must be 0 or greater'),
  billingType: yup.string(),
  freeMinutes: yup.number().transform((value, originalValue) => originalValue === '' ? 0 : value).min(0, 'Must be 0 or greater'),
  guestLimit: yup.number().transform((value, originalValue) => originalValue === '' ? 0 : value).min(0, 'Must be 0 or greater'),
  minutesLimit: yup.number().transform((value, originalValue) => originalValue === '' ? 0 : value).min(0, 'Must be 0 or greater'),
  overageRate: yup.number().transform((value, originalValue) => originalValue === '' ? 0 : value).min(0, 'Must be 0 or greater'),
  reservationRate: yup.number().transform((value, originalValue) => originalValue === '' ? 0 : value).min(0, 'Must be 0 or greater'),
  hourlyCap: yup.number().transform((value, originalValue) => originalValue === '' ? 0 : value).min(0, 'Must be 0 or greater'),
  dailyCap: yup.number().transform((value, originalValue) => originalValue === '' ? 0 : value).min(0, 'Must be 0 or greater'),
  capDurationMinutes: yup.number().transform((value, originalValue) => originalValue === '' ? 0 : value).min(0, 'Must be 0 or greater'),
  reservationTimeLimit: yup.number().transform((value, originalValue) => originalValue === '' ? 0 : value).min(0, 'Must be 0 or greater'),
  taxPercentage: yup.number().transform((value, originalValue) => originalValue === '' ? 0 : value).min(0, 'Must be 0 or greater').max(100, 'Must be 100 or less'),
  isSubscription: yup.boolean(),
  includeFreeMinutes: yup.boolean(),
  subscriptionFee: yup.number().transform((value, originalValue) => originalValue === '' ? 0 : value).min(0, 'Must be 0 or greater'),
  sortOrder: yup.number().transform((value, originalValue) => originalValue === '' ? 0 : value).min(0, 'Must be 0 or greater'),
  features: yup.string(),
})

// Human label for the Unit Rate field based on the selected billing type
const unitRateMeta = (billingType) => {
  if (billingType === 'per_hour') return { label: 'Unit Rate (per hour)', info: 'Variable cost per hour of active transit.' }
  if (billingType === 'flat_fee') return { label: 'Unit Rate (flat)', info: 'Flat fee per ride — no time-based charge (covered by unlock fee).' }
  return { label: 'Unit Rate (per minute)', info: 'Variable cost per minute of active transit.' }
}

const PlanForm = ({ initialData, onSubmit, onCancel, forceSubscription = null, isInline = false }) => {
  const { operator } = useOperator()

  const initState = {
    productname: initialData?.productname || '',
    description: initialData?.description || '',
    amount: initialData?.amount || 0,
    currency: initialData?.currency || 'USD',
    interval: initialData?.interval || 'month',
    unlockFee: initialData?.unlockFee || 0,
    guestBikeUnlockFee: initialData?.guestBikeUnlockFee || 0,
    unitFee: initialData?.unitFee || 0,
    billingType: initialData?.billingType || 'per_minute',
    freeMinutes: initialData?.freeMinutes || 0,
    includeFreeMinutes: initialData?.includeFreeMinutes || false,
    guestLimit: initialData?.guestLimit || 0,
    minutesLimit: initialData?.minutesLimit || 0,
    overageRate: initialData?.overageRate || 0,
    reservationRate: initialData?.reservationRate || 0,
    hourlyCap: initialData?.hourlyCap || 0,
    dailyCap: initialData?.dailyCap || 0,
    capDurationMinutes: initialData?.capDurationMinutes || 0,
    reservationTimeLimit: initialData?.reservationTimeLimit || 0,
    taxPercentage: initialData?.taxPercentage || 0,
    isSubscription: forceSubscription !== null ? forceSubscription : (initialData?.isSubscription || false),
    subscriptionFee: initialData?.subscriptionFee || 0,
    sortOrder: initialData?.sortOrder || 0,
    features: initialData?.features || '',
    isPromo: initialData?.isPromo || false,
    operatorId: operator?._id,
    ...initialData
  }

  return (
    <Formik
      initialValues={initState}
      validationSchema={PlanValidationSchema}
      onSubmit={onSubmit}
      enableReinitialize={true}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue }) => (
        <Form className={`w-full ${isInline ? '' : 'max-w-5xl'}`}>
          <div className={`${isInline ? '' : 'p-4 md:p-6'}`}>
            {/* Form Sections */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10'>
              {/* Basic Info */}
              <div className='space-y-4'>
                <h3 className='text-xs font-bold uppercase tracking-widest text-[#BA141B] mb-2 flex items-center gap-2'>
                  <span className='w-1 h-4 bg-[#BA141B] rounded-full'></span>
                  Core Configuration
                </h3>
                <InputFieldOutlined
                  label={<LabelWithInfo label='Plan Identity' info='Name and identifier for this billing schema.' />}
                  name='productname'
                  type='text'
                  placeholder='e.g. PREMIUM ACCESS'
                />
                <InputFieldOutlined
                  label={<LabelWithInfo label='Operational Summary' info='Internal dossier description of this plan’s purpose.' />}
                  name='description'
                  type='text'
                  placeholder='e.g. Standard urban transit schema'
                />
                <Select
                  label={<LabelWithInfo label='Operational Currency' info='The legal tender used for all transactions in this plan.' />}
                  name='currency'
                  className='!bg-gray-50 !px-4 !rounded-lg !border-[#BA141B]/60'
                  options={[
                    { label: 'USD - US Dollar', value: 'USD' },
                    { label: 'CAD - Canadian Dollar', value: 'CAD' },
                    { label: 'EUR - Euro', value: 'EUR' },
                    { label: 'GBP - British Pound', value: 'GBP' },
                  ]}
                />
                <InputFieldOutlined
                  label={<LabelWithInfo label='Sort Order' info='Display position in plan lists (lower shows first). Leave 0 to sort by lowest price.' />}
                  name='sortOrder'
                  type='number'
                  placeholder='0'
                />
              </div>

              {/* Pricing Section */}
              <div className='space-y-4'>
                <h3 className='text-xs font-bold uppercase tracking-widest text-emerald-600 mb-2 flex items-center gap-2'>
                  <span className='w-1 h-4 bg-emerald-600 rounded-full'></span>
                  Pricing Metrics
                </h3>
                <div className='grid grid-cols-2 gap-x-4 gap-y-5'>
                  <InputFieldOutlined
                    label={<LabelWithInfo label='Unlock Fee' info='Initial strike price to engage the vehicle.' />}
                    name='unlockFee'
                    type='number'
                    placeholder='0.00'
                  />
                  <InputFieldOutlined
                    label={<LabelWithInfo label='Guest Bike Unlock Fee' info='Unlock fee charged when starting a guest bike ride.' />}
                    name='guestBikeUnlockFee'
                    type='number'
                    placeholder='0.00'
                  />
                  <InputFieldOutlined
                    label={<LabelWithInfo {...unitRateMeta(values.billingType)} />}
                    name='unitFee'
                    type='number'
                    placeholder='0.00'
                  />
                  <Select
                    label={<LabelWithInfo label='Billing Type' info='The calculation logic for usage fees.' />}
                    name='billingType'
                    className='!bg-gray-50 !px-4 !rounded-lg !border-[#BA141B]/60 col-span-2 md:col-span-1'
                    options={[
                      { label: 'Per Minute', value: 'per_minute' },
                      { label: 'Per Hour', value: 'per_hour' },
                      { label: 'Flat Fee', value: 'flat_fee' },
                    ]}
                  />
                  
                </div>
              </div>

              {/* Logical Limits */}
              <div className='space-y-4'>
                <h3 className='text-xs font-bold uppercase tracking-widest text-blue-600 mb-2 flex items-center gap-2'>
                  <span className='w-1 h-4 bg-blue-600 rounded-full'></span>
                  Governance & Limits
                </h3>
                <div className='grid grid-cols-2 gap-x-4 gap-y-5'>
                  <InputFieldOutlined
                    label={<LabelWithInfo label='Free Minutes' info='Free-minute allowance. For PAYGO + Promo this is a one-time allowance that never resets. For subscriptions it applies on every ride only when "Include free minutes" is enabled.' />}
                    name='freeMinutes'
                    type='number'
                  />
                  <InputFieldOutlined 
                    label={<LabelWithInfo label='Guest Limit' info='Maximum number of extra guests allowed to be added to the ride.' />} 
                    name='guestLimit' 
                    type='number' 
                  />
                  <InputFieldOutlined 
                    label={<LabelWithInfo label='Minutes Limit' info='Maximum duration allowed for a single ride session before overage applies.' />} 
                    name='minutesLimit' 
                    type='number' 
                  />
                  <InputFieldOutlined 
                    label={<LabelWithInfo label='Tax %' info='Standard tax rate applied to the total transaction amount (e.g. 14).' />} 
                    name='taxPercentage' 
                    type='number' 
                  />
                  <InputFieldOutlined 
                    label={<LabelWithInfo label='Daily Cap' info='Maximum total billing amount permitted within a 24-hour cycle.' />} 
                    name='dailyCap' 
                    type='number' 
                  />
                  <InputFieldOutlined 
                    label={<LabelWithInfo label='Hourly Cap' info='Maximum billing ceiling per individual hour of active use.' />} 
                    name='hourlyCap' 
                    type='number' 
                  />
                  <InputFieldOutlined 
                    label={<LabelWithInfo label='Cap Duration (Min)' info='The maximum time window the rate cap remains active.' />} 
                    name='capDurationMinutes' 
                    type='number' 
                  />
                  <InputFieldOutlined 
                    label={<LabelWithInfo label='Overage Rate' info='Fee applied per minute after session limit is exceeded.' />}
                    name='overageRate' 
                    type='number' 
                  />
                </div>
              </div>

              {/* Dynamic Fees Section */}
              <div className='space-y-4'>
                <h3 className='text-xs font-bold uppercase tracking-widest text-orange-600 mb-2 flex items-center gap-2'>
                  <span className='w-1 h-4 bg-orange-600 rounded-full'></span>
                  Reservaions
                </h3>
                <div className='grid grid-cols-2 gap-x-4 gap-y-5'>
                  <InputFieldOutlined 
                    label={<LabelWithInfo label='Reservation Rate' info='Cost per minute while the asset is on hold.' />}
                    name='reservationRate' 
                    type='number' 
                  />
                   <InputFieldOutlined 
                    label={<LabelWithInfo label='Res. Time Limit' info='Maximum minutes an asset can stay reserved.' />}
                    name='reservationTimeLimit' 
                    type='number' 
                  />
                  
                </div>
              </div>
            </div>

            {/* Comprehensive Features */}
            <div className='mt-8 pt-6 border-t border-gray-100'>
              <div className='flex flex-col lg:flex-row gap-8'>
                <div className='flex-1 space-y-4'>
                  <div className='flex-col items-center justify-between'>
                    <div className='flex items-center justify-between'>
                    <LabelWithInfo label='Value Proposition & Features' info='List of features shown to the end-user (one per line).' />
                    <div className='flex items-center gap-4'>
                    {forceSubscription === null && (
                      <label className='flex items-center cursor-pointer gap-2 group'>
                        <input
                          type='checkbox'
                          name='isSubscription'
                          checked={values.isSubscription}
                          onChange={() => setFieldValue('isSubscription', !values.isSubscription)}
                          className='w-4 h-4 text-[#BA141B] accent-[#BA141B] rounded focus:ring-0 transition-all'
                        />
                        <span className='text-[10px] font-black uppercase tracking-widest text-gray-400 group-hover:text-[#BA141B] transition-colors'>Enroll as Subscription</span>
                      </label>
                    )}
                    {values.isSubscription && (
                      <label className='flex items-center cursor-pointer gap-2 group' title='When on, this subscription grants the Free Minutes above on every ride.'>
                        <input
                          type='checkbox'
                          name='includeFreeMinutes'
                          checked={values.includeFreeMinutes}
                          onChange={() => setFieldValue('includeFreeMinutes', !values.includeFreeMinutes)}
                          className='w-4 h-4 accent-green-600 rounded focus:ring-0 transition-all'
                        />
                        <span className='text-[10px] font-black uppercase tracking-widest text-gray-400 group-hover:text-green-600 transition-colors'>Include Free Minutes</span>
                      </label>
                    )}
                    <label className='flex items-center cursor-pointer gap-2 group' title='One-time promo: the Free Minutes above are granted once and never reset. PAYGO only.'>
                      <input
                        type='checkbox'
                        name='isPromo'
                        checked={values.isPromo}
                        onChange={() => setFieldValue('isPromo', !values.isPromo)}
                        className='w-4 h-4 accent-amber-500 rounded focus:ring-0 transition-all'
                      />
                      <span className='text-[10px] font-black uppercase tracking-widest text-gray-400 group-hover:text-amber-500 transition-colors'>Promo (one-time)</span>
                    </label>
                    </div>
                    </div>
                  <div className='flex items-center justify-between gap-4'>
                    {values.isSubscription && (
                    <InputFieldOutlined
                      label={<LabelWithInfo label='Subscription Fee' info='Fixed recurring cost for subscription access.' />}
                      name='subscriptionFee'
                      type='number'
                      placeholder='0.00'
                    />
                  )}
                  {values.isSubscription && (
                    <Select
                      label={<LabelWithInfo label='Cycle' info='Billing frequency for this plan.' />}
                      name='interval'
                      className='!bg-gray-50 !px-4 !rounded-lg !border-[#BA141B]/60'
                      options={[
                        { label: 'Daily', value: 'day' },
                        { label: 'Monthly', value: 'month' },
                      ]}
                    />
                  )}
                  </div>

                  </div>
                  <textarea
                    name='features'
                    value={values.features}
                    onChange={(e) => setFieldValue('features', e.target.value)}
                    placeholder='🚴 Unlimited Access&#10;🛡️ Full Insurance&#10;⚡ Zero Unlock Fees'
                    className='w-full p-4 rounded-2xl bg-gray-50/50 border-2 border-[#BA141B]/20 focus:border-[#BA141B]/50 transition-all text-sm font-medium h-32 outline-none'
                  />
                </div>
                
                {/* Submit Controls */}
                <div className='flex flex-col justify-end gap-3'>
                  <div className='flex gap-3'>
                    {onCancel && (
                      <SecondaryBtn onClick={onCancel} label='Discard' className='px-8 h-12' />
                    )}
                    <Button processing={isSubmitting} type='submit' className='px-12 h-12 flex-1'>
                      {initialData?._id ? 'Synchronize Data' : 'Execute Creation'}
                    </Button>
                  </div>
                  {isInline && (
                    <p className='text-[9px] font-bold text-gray-400 uppercase tracking-widest text-center'>Last update: {new Date().toLocaleTimeString()}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default PlanForm
